
import { defineComponent, ref, computed, onMounted, nextTick } from "vue";
import { usePlugin } from "@/plugins";
import {
  OnthejobDocument,
  OnthejobItem,
  UpsertOnTheJobDocumentRequest,
  GetOnTheJobDocumentRequest,
  GetOnTheJobDocumentResponse,
  UpsertOnTheJobDocumentResponse,
  DownloadOnTheJobDocumentRequest,
  CancelOnTheJobDocumentRequest,
} from "@/types";
import InputCourse from "@/components/InputCourse.vue";
import InputUser from "@/components/InputUser.vue";
import InputOnTheJobScore from "@/components/InputOnTheJobScore.vue";
import AttachmentPanel from "@/components/AttachmentPanel.vue";
import useVuelidate from "@vuelidate/core";
import StatusBadge from "@/components/StatusBadge.vue";
import CancelButton from "@/components/CancelButton.vue";
import { required, requiredIf, helpers } from "@vuelidate/validators";
import { saveAs } from "file-saver";
import { useConfirm } from "primevue/useconfirm";
import { useSessionState } from "@/store";

export default defineComponent({
  name: "OnTheJobDocumentScreen",
  components: {
    InputCourse,
    InputUser,
    InputOnTheJobScore,
    AttachmentPanel,
    StatusBadge,
    CancelButton,
  },
  setup() {
    const {
      axios,
      error,
      success,
      setLoading,
      dayjs,
      route,
      onRouterReplace,
      isAdmin,
      useSessionState,
      formatAppDateTime,
      confirmation,
    } = usePlugin();
    const initialData: OnthejobDocument = {
      id: null,
      documentDate: dayjs().toDate(),
      submittedDate: null,
      location: null,
      courseId: null,
      body: null,
      items: [],
      createById: null,
      createBy: null,
      course: null,
      cancelOn: null,
      cancelById: null,
      cancelBy: null,
      status: null,
    };
    const sessionState = useSessionState();
    const onTheJobDocument = ref<OnthejobDocument>(initialData);
    const selectedTopic = ref("");
    const mainTopic = ref("");
    const optionTopic = ref("");
    const otherTopic = ref("");
    const submitting = ref(false);
    const description = {
      main: "ทักษะ/ความรู้จำเป็นในการทำงานเรื่อง",
      optional: "ทักษะ/ความรู้เสริมเรื่อง",
      other: "อื่น ๆ",
    };

    const rules = computed(() => ({
      onTheJobDocument: {
        documentDate: { required },
        location: { required },
        courseId: { required },
      },
      selectedTopic: { required },
      mainTopic: {
        requiredIf: requiredIf(() => {
          return selectedTopic.value === description.main;
        }),
      },
      optionTopic: {
        requiredIf: requiredIf(() => {
          return selectedTopic.value === description.optional;
        }),
      },
      otherTopic: {
        requiredIf: requiredIf(() => {
          return selectedTopic.value === description.other;
        }),
      },
    }));
    const v$ = useVuelidate(
      rules,
      {
        onTheJobDocument,
        selectedTopic,
        mainTopic,
        optionTopic,
        otherTopic,
      },
      { $autoDirty: true }
    );
    const onSaveDocument = async () => {
      submitting.value = false;
      await nextTick();
      v$.value.$reset();
      v$.value.$touch();
      if (!(await v$.value.$validate())) return;

      setLoading(true);
      try {
        onTheJobDocument.value.body = `${selectedTopic.value}${
          selectedTopic.value === description.main
            ? mainTopic.value
            : selectedTopic.value === description.optional
            ? optionTopic.value
            : otherTopic.value
        }`;
        var result = await axios.post<UpsertOnTheJobDocumentResponse>(
          "api/v1/services/onTheJobDocument/upsert",
          {
            document: onTheJobDocument.value,
          } as UpsertOnTheJobDocumentRequest
        );
        if (!route.params["id"]) {
          await onRouterReplace({
            name: "editOnTheJobDocument",
            params: { id: result.data.documentId },
          });
          await loadDocument();
        } else {
          await loadDocument();
        }
      } catch (e) {
        error(e);
      } finally {
        setLoading(false);
      }
    };

    const loadDocument = async () => {
      if (!route.params["id"]) return;
      setLoading(true);
      try {
        var result = await axios.post<GetOnTheJobDocumentResponse>(
          "api/v1/services/onTheJobDocument",
          {
            id: route.params["id"],
          } as GetOnTheJobDocumentRequest
        );
        if ((result.data.onTheJobDocuments?.length ?? 0) > 0) {
          onTheJobDocument.value = result.data.onTheJobDocuments[0];
          onTheJobDocument.value.documentDate = dayjs(
            onTheJobDocument.value.documentDate ?? ""
          ).toDate();
          if (onTheJobDocument.value.body?.startsWith(description.main)) {
            selectedTopic.value = description.main;
            mainTopic.value = onTheJobDocument.value.body.replace(
              description.main,
              ""
            );
          }
          if (onTheJobDocument.value.body?.startsWith(description.optional)) {
            selectedTopic.value = description.optional;
            optionTopic.value = onTheJobDocument.value.body.replace(
              description.optional,
              ""
            );
          }
          if (onTheJobDocument.value.body?.startsWith(description.other)) {
            selectedTopic.value = description.other;
            otherTopic.value = onTheJobDocument.value.body.replace(
              description.other,
              ""
            );
          }
        } else {
          throw Error(
            "ไม่สามารถดึงข้อมูลได้ กรุณาตรวจสอบสิทธิการใช้งานระบบอีกครั้ง"
          );
        }
      } catch (e) {
        error(e);
      } finally {
        setLoading(false);
      }
    };

    const onSubmitDocument = async () => {
      confirmation.require({
        header: "ส่งเอกสารให้ HRD",
        message: "กรุณายืนยันเพื่อส่งเอกสารให้ HRD",
        acceptLabel: "ยืนยันส่งเอกสาร",
        rejectLabel: "ปิด",
        accept: async () => {
          submitting.value = true;
          await nextTick();
          v$.value.$reset();
          v$.value.$touch();
          if (!(await v$.value.$validate())) return;

          onTheJobDocument.value.submittedDate = dayjs().toDate();
          await onSaveDocument();
        },
      });
    };

    const onExportDocument = async () => {
      submitting.value = false;
      await nextTick();
      v$.value.$reset();
      v$.value.$touch();
      if (!(await v$.value.$validate())) return;

      confirmation.require({
        header: "แสดงคะแนนหรือไม่?",
        message: "คุณต้องการแสดงคะแนนด้วยหรือไม่?",
        acceptLabel: "แสดงคะแนน",
        rejectLabel: "ไม่แสดงคะแนน",
        accept: async () => {
          setLoading(true);
          try {
            const result = await axios.post(
              "api/v1/services/onTheJobDocument/download",
              {
                documentNo: onTheJobDocument.value.id,
                printScore: true,
              } as DownloadOnTheJobDocumentRequest,
              { responseType: "blob" }
            );
            saveAs(result.data, `${onTheJobDocument.value.id}.docx`);
          } catch (e) {
            error(e);
          } finally {
            setLoading(false);
          }
        },
        reject: async () => {
          setLoading(true);
          try {
            const result = await axios.post(
              "api/v1/services/onTheJobDocument/download",
              {
                documentNo: onTheJobDocument.value.id,
                printScore: false,
              } as DownloadOnTheJobDocumentRequest,
              { responseType: "blob" }
            );
            saveAs(result.data, `${onTheJobDocument.value.id}.docx`);
          } catch (e) {
            error(e);
          } finally {
            setLoading(false);
          }
        },
      });
    };
    const onAddAttendee = () => {
      onTheJobDocument.value.items?.push({
        onthejobDocumentId: null,
        employeeId: null,
        score: null,
        employee: null,
      } as OnthejobItem);
    };

    const onDeleteAttendee = (index: number) => {
      onTheJobDocument.value.items?.splice(index, 1);
    };
    const onCancelDocument = async () => {
      setLoading(true);
      try {
        await axios.post("api/v1/services/onTheJobDocument/cancel", {
          id: onTheJobDocument.value.id,
        } as CancelOnTheJobDocumentRequest);
        success("ยกเลิกเอกสารเรียบร้อยแล้ว");
        await loadDocument();
      } catch (e) {
        error(e);
      } finally {
        setLoading(false);
      }
    };
    const statusText = computed(() => {
      // return onTheJobDocument.value.submittedDate
      //   ? "ส่งเอกสารแล้ว"
      //   : onTheJobDocument.value.id
      //   ? "บันทึก"
      //   : "สร้างใหม่";
      return onTheJobDocument.value.status ?? "ยังไม่บันทึก";
    });

    const documentId = computed(() => onTheJobDocument.value.id || "N/A");
    const isReadonly = computed(
      () =>
        !!onTheJobDocument.value.submittedDate ||
        !!onTheJobDocument.value.cancelOn
    );

    onMounted(() => loadDocument());

    return {
      onTheJobDocument,
      onSaveDocument,
      onSubmitDocument,
      onExportDocument,
      documentId,
      onAddAttendee,
      onDeleteAttendee,
      onCancelDocument,
      v$,

      selectedTopic,
      mainTopic,
      optionTopic,
      otherTopic,
      submitting,
      description,
      statusText,
      isReadonly,
      isAdmin,
      formatAppDateTime,
      sessionState,
    };
  },
});
